import type { ShareLink } from "../api.ts";

export const SHARE_PASSWORD_MIN = 4;

export type ExpiryChoice = "never" | "1d" | "7d" | "30d" | "date";

export const EXPIRY_CHOICES: { value: ExpiryChoice; label: string; days?: number }[] = [
  { value: "never", label: "Never" },
  { value: "1d", label: "1 day", days: 1 },
  { value: "7d", label: "7 days", days: 7 },
  { value: "30d", label: "30 days", days: 30 },
  { value: "date", label: "Pick a date" },
];

const DAY_MS = 86_400_000;

const pad = (value: number) => String(value).padStart(2, "0");

/** A local calendar date as the `YYYY-MM-DD` a date input reads and writes. */
export const dateInputValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** When a link made now with this choice stops opening; a picked date ends at local midnight. */
export const expiryOf = (choice: ExpiryChoice, picked: string, now: Date): Date | null => {
  if (choice === "never") return null;
  if (choice === "date") {
    const [year, month, day] = picked.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day + 1);
  }
  const days = EXPIRY_CHOICES.find((option) => option.value === choice)?.days ?? 0;
  return new Date(now.getTime() + days * DAY_MS);
};

export const shortShareToken = (token: string): string =>
  token.length > 12 ? `${token.slice(0, 6)}…${token.slice(-4)}` : token;

/** `f.example.com/s/q3Jx9v…K7jM`: enough of the token to tell links apart. */
export const shortShareUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.host}/s/${shortShareToken(parsed.pathname.split("/").pop() ?? "")}`;
  } catch {
    return url;
  }
};

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const STEPS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * DAY_MS],
  ["month", 60 * DAY_MS],
  ["day", DAY_MS],
  ["hour", 3_600_000],
  ["minute", 60_000],
];

/** "in 6 days", "2 hours ago", "just now". Days run up to two months before months take over. */
export const formatRelative = (iso: string, now = new Date()): string => {
  const delta = Date.parse(iso) - now.getTime();
  for (const [unit, size] of STEPS) {
    if (Math.abs(delta) < size) continue;
    const count = unit === "month" ? Math.round(delta / (30 * DAY_MS)) : Math.round(delta / size);
    return relative.format(count, unit);
  }
  return "just now";
};

export const endedAt = (share: ShareLink): string | null =>
  share.state === "revoked" ? share.revokedAt : share.state === "expired" ? share.expiresAt : null;

export const splitShares = (items: ShareLink[]) => ({
  active: items.filter((share) => share.state === "active"),
  ended: items
    .filter((share) => share.state !== "active")
    .sort((a, b) => Date.parse(endedAt(b) ?? "") - Date.parse(endedAt(a) ?? "")),
});
